import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { resolveMediaUrl } from "../../lib/media-url";
import type { Question } from "./types";

function gridClassFor(count: number): string {
  if (count <= 1) return "grid-cols-1";
  return "grid-cols-1 md:grid-cols-2";
}

export function QuestionCard({
  question,
  imageAltTexts,
  audioFailed,
  onRetryAudio,
}: {
  question: Question;
  imageAltTexts?: string[];
  audioFailed?: boolean;
  onRetryAudio?: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  function pauseOtherAudio() {
    if (typeof document === "undefined") return;
    document.querySelectorAll("audio").forEach((a) => {
      if (a !== audioRef.current && !a.paused) a.pause();
    });
  }

  useEffect(() => {
    setAudioBlocked(false);
    setAudioPlaying(false);
    if (!question.questionAudioUrl || !audioRef.current) return;
    pauseOtherAudio();
    audioRef.current
      .play()
      .then(() => setAudioPlaying(true))
      .catch(() => setAudioBlocked(true));
  }, [question.questionAudioUrl]);

  function toggleAudio() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      pauseOtherAudio();
      a.play()
        .then(() => {
          setAudioPlaying(true);
          setAudioBlocked(false);
        })
        .catch(() => {});
    } else {
      a.pause();
      setAudioPlaying(false);
    }
  }

  return (
    <article className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {question.part === 1 ? "Deel 1 · video-vraag" : `Deel ${question.part}`}
        </span>
        {question.topicTag ? (
          <span className="font-serif-italic text-sm text-[var(--ink-faint)]">
            {question.topicTag}
          </span>
        ) : null}
      </div>

      {question.imageUrls.length > 0 ? (
        <div className={cn("grid gap-3", gridClassFor(question.imageUrls.length))}>
          {question.imageUrls.map((u, i) => (
            <figure key={u} className="relative">
              <img
                src={resolveMediaUrl(u)}
                alt={imageAltTexts?.[i] ?? `Foto ${i + 1}`}
                loading="lazy"
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel-soft)] object-cover aspect-[4/3]"
              />
              {question.imageUrls.length > 1 ? (
                <span
                  aria-hidden
                  className="absolute top-2 left-2 inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-black/65 px-1.5 font-mono-badge text-xs font-semibold text-[var(--ink)] backdrop-blur-sm"
                >
                  {i + 1}
                </span>
              ) : null}
            </figure>
          ))}
        </div>
      ) : null}

      <p className="font-serif-italic text-2xl leading-relaxed text-[var(--ink)]">
        {question.promptText}
      </p>

      {!question.questionAudioUrl && !audioFailed ? (
        <p
          role="status"
          aria-live="polite"
          className="text-xs text-[var(--ink-faint)] italic"
        >
          Audio wordt voorbereid…
        </p>
      ) : null}
      {!question.questionAudioUrl && audioFailed ? (
        <p role="status" className="text-xs text-[var(--warn)] italic flex items-center gap-2">
          <span>Audio is nu niet beschikbaar — lees de vraag hierboven.</span>
          {onRetryAudio ? (
            <button
              type="button"
              onClick={onRetryAudio}
              className="text-[var(--accent)] hover:text-[var(--accent-hover)] underline-offset-2 hover:underline"
            >
              Probeer opnieuw
            </button>
          ) : null}
        </p>
      ) : null}
      {question.questionAudioUrl ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleAudio}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
              audioPlaying
                ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--ink)]"
                : "border-[var(--border)] bg-[var(--panel)] hover:bg-[var(--panel-soft)] text-[var(--ink-muted)]",
            )}
            aria-pressed={audioPlaying}
            aria-label={audioPlaying ? "Pauze" : "Hoor de vraag"}
          >
            <span className="font-mono-badge text-xs">
              {audioPlaying ? "⏸" : "▶"}
            </span>
            <span>{audioBlocked ? "Tik om de vraag te horen" : "Hoor de vraag opnieuw"}</span>
          </button>
          <audio
            ref={audioRef}
            src={resolveMediaUrl(question.questionAudioUrl)}
            playsInline
            preload="auto"
            onEnded={() => setAudioPlaying(false)}
            onPause={() => setAudioPlaying(false)}
            onPlay={() => setAudioPlaying(true)}
            className="sr-only"
            aria-hidden
          />
        </div>
      ) : null}
    </article>
  );
}
